import { useEffect, useState, useCallback } from "react";
import {
  Search, ClipboardList, ChevronLeft, ChevronRight,
  Shield, School, Users, Settings, Trash2, CheckCircle,
  XCircle, Plus, Edit, AlertCircle, Filter,
} from "lucide-react";
import SuperAdminLayout from "@/components/ui/superadmin/SuperAdminLayout";
import { superAdminApi, type AuditLogEntry } from "@/hooks/api/superAdminApi";

const ACTION_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  school_created:       { label: "School Created",       icon: Plus,         color: "text-emerald-400 bg-emerald-500/10" },
  school_updated:       { label: "School Updated",       icon: Edit,         color: "text-blue-400 bg-blue-500/10" },
  school_deleted:       { label: "School Deleted",       icon: Trash2,       color: "text-red-400 bg-red-500/10" },
  school_status_changed:{ label: "School Status Changed",icon: AlertCircle,  color: "text-amber-400 bg-amber-500/10" },
  user_suspended:       { label: "User Suspended",       icon: XCircle,      color: "text-amber-400 bg-amber-500/10" },
  user_activated:       { label: "User Activated",       icon: CheckCircle,  color: "text-emerald-400 bg-emerald-500/10" },
  user_deleted:         { label: "User Deleted",         icon: Trash2,       color: "text-red-400 bg-red-500/10" },
  subscription_updated: { label: "Subscription Updated", icon: Settings,     color: "text-violet-400 bg-violet-500/10" },
  admin_login:          { label: "Admin Login",          icon: Shield,       color: "text-teal-400 bg-teal-500/10" },
};

const TARGET_ICONS: Record<string, React.ElementType> = {
  school: School,
  user:   Users,
  system: Shield,
};

function ActionBadge({ action }: { action: string }) {
  const meta = ACTION_META[action];
  const Icon = meta?.icon ?? AlertCircle;
  const color = meta?.color ?? "text-slate-400 bg-slate-700";
  const label = meta?.label ?? action.replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold capitalize ${color}`}>
      <Icon className="w-3 h-3 shrink-0" />
      {label}
    </span>
  );
}

function MetaPanel({ meta }: { meta: Record<string, unknown> }) {
  const entries = Object.entries(meta).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {entries.slice(0, 4).map(([k, v]) => (
        <span key={k} className="text-xs text-slate-500 bg-slate-800 rounded px-1.5 py-0.5">
          <span className="text-slate-600">{k}:</span> {String(v)}
        </span>
      ))}
    </div>
  );
}

const ACTION_TYPES = [
  "school_created", "school_updated", "school_deleted", "school_status_changed",
  "user_suspended", "user_activated", "user_deleted", "subscription_updated", "admin_login",
];

export default function AuditLogPage() {
  const [entries, setEntries]     = useState<AuditLogEntry[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await superAdminApi.getAuditLogs({
        page: p, limit: 25,
        search: search || undefined,
        action: actionFilter || undefined,
      });
      setEntries(res.logs);
      setTotal(res.total);
      setPages(res.pages);
    } catch {
      // empty state handles display
    } finally {
      setLoading(false);
    }
  }, [page, search, actionFilter]);

  useEffect(() => { load(1); setPage(1); }, [search, actionFilter]);
  useEffect(() => { load(page); }, [page]);

  return (
    <SuperAdminLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="text-slate-400 text-sm mt-0.5">{total.toLocaleString()} total admin actions recorded</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search by actor or target…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500 shrink-0" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option value="">All actions</option>
              {ACTION_TYPES.map((a) => (
                <option key={a} value={a}>{ACTION_META[a]?.label ?? a}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Log table */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Actor</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Target</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading ? (
                  Array.from({ length: 12 }).map((_, i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4].map((j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-slate-800 rounded animate-pulse" style={{ width: `${50 + (j * 19) % 45}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-slate-500">
                      <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>No audit entries found</p>
                    </td>
                  </tr>
                ) : (
                  entries.map((e) => {
                    const TargetIcon = TARGET_ICONS[e.targetType] ?? AlertCircle;
                    const when = new Date(e.createdAt);
                    return (
                      <tr key={e._id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-5 py-4">
                          <ActionBadge action={e.action} />
                          {e.meta && Object.keys(e.meta).length > 0 && <MetaPanel meta={e.meta} />}
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <p className="text-sm font-medium text-white">{e.actorName}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{e.actorEmail}</p>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          {e.targetId ? (
                            <div className="flex items-center gap-2">
                              <TargetIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                              <span className="text-xs text-slate-400 font-mono truncate max-w-40">
                                {e.targetName ?? e.targetId}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-600">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-xs text-slate-500 whitespace-nowrap">
                          <p>{when.toLocaleDateString()}</p>
                          <p className="text-slate-600">{when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-800">
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
